import { Client } from "../../clients/client";
import moment from "moment";

import { PaginationForm, Pagination } from "@/common/mixins/pagination";
import {
  PromotionCondition,
  PromotionConditionItem,
  PromotionConditionItemGroups,
  PromotionItemGroups,
  PromotionItem
} from "@/clients/PromotionClient";
import {
  PromotionCreateForm,
  PromotionGroupForm,
  PromotionItemGroupForm,
  PromotionItemForm,
  PromotionConditionPriceForm,
  PromotionItemStatus
} from "../promotion/forms/PromotionCreateForm";
import { PromotionDetailForm } from "../promotion/forms/PromotionDetailForm";
import { Promotion } from "./models/Promotion";
import { PromotionResult } from "./models/PromotionResult";
import { PromotionCheckForm } from "./forms/PromotionCheckForm";
import { PromotionUpdateForm } from "./forms/PromotionUpdateForm";

export class PromotionUseCase {
  constructor(private client: Client) {}

  public async createPromotion(
    form: PromotionCreateForm
  ): Promise<PromotionCreateForm> {
    if (
      this.convertConditionType(form.promotionGroup.conditionType) === "item"
    ) {
      let orderItemLimit: number | null = null;
      if (
        form.promotionGroup.orderItemLimit &&
        form.promotionGroup.orderItemLimitAmount !== null
      ) {
        orderItemLimit = +form.promotionGroup.orderItemLimitAmount;
      }
      return this.client.promotions.postPromotions({
        shopId: form.shop!.id,
        name: form.name,
        startDate: moment(form.startDate!).toISOString(),
        endDate: form.endDate ? moment(form.endDate).toISOString() : undefined,
        orderLimit: +form.promotionGroup.orderLimit,
        orderItemLimit,
        channels: form.promotionGroup.channelsSelected,
        holdWhenDepleted: form.promotionGroup.promotionHold,
        conditions: this.formatConditions(form.promotionGroup),
        inuse: form.inuse
      });
    } else {
      return this.client.promotions.postPromotions({
        shopId: form.shop!.id,
        name: form.name,
        startDate: moment(form.startDate!).toISOString(),
        endDate: form.endDate ? moment(form.endDate).toISOString() : undefined,
        orderLimit: +form.promotionGroup.orderLimit,
        channels: form.promotionGroup.channelsSelected,
        holdWhenDepleted: form.promotionGroup.promotionHold,
        conditions: this.formatPriceConditions(form.promotionGroup),
        inuse: form.inuse
      });
    }
  }
  private formatConditions(
    promotionGroup: PromotionGroupForm
  ): PromotionCondition[] {
    const createPromotionConditionItemGroups: PromotionConditionItemGroups[] = promotionGroup.conditionItemGroups.map(
      (conditionItemGroup: PromotionItemGroupForm) => {
        const items: PromotionConditionItem[] = conditionItemGroup.items.map(
          (item: PromotionItemForm) => {
            return { itemSku: item.sku, quantity: item.quantity };
          }
        );
        return { items };
      }
    );

    const createPromotionItemGroups: PromotionItemGroups[] = promotionGroup.promotionItemGroups.map(
      (promotionItemGroup: PromotionItemGroupForm) => {
        const items: PromotionItem[] = promotionItemGroup.items.map(
          (item: PromotionItemForm) => {
            return { itemSku: item.sku, quantity: item.quantity };
          }
        );
        return { items };
      }
    );

    return [
      {
        type: this.convertConditionType(promotionGroup.conditionType),
        conditionItemGroups: createPromotionConditionItemGroups,
        itemGroups: createPromotionItemGroups
      }
    ];
  }
  private updateFormatConditions(
    promotionGroup: PromotionGroupForm
  ): PromotionCondition[] {
    const createPromotionConditionItemGroups: PromotionConditionItemGroups[] = promotionGroup.conditionItemGroups.map(
      (conditionItemGroup: PromotionItemGroupForm) => {
        const items: PromotionConditionItem[] = conditionItemGroup.items.map(
          (item: PromotionItemForm) => {
            return { id: item.id, itemSku: item.sku, quantity: item.quantity };
          }
        );
        return {
          id: conditionItemGroup.id,
          items,
          deletePromotionConditionItemId: conditionItemGroup.deletedItemsId
        };
      }
    );

    const createPromotionItemGroups: PromotionItemGroups[] = promotionGroup.promotionItemGroups.map(
      (promotionItemGroup: PromotionItemGroupForm) => {
        const items: PromotionItem[] = promotionItemGroup.items.map(
          (item: PromotionItemForm) => {
            return { id: item.id, itemSku: item.sku, quantity: item.quantity };
          }
        );
        return {
          id: promotionItemGroup.id,
          items,
          deletePromotionItemId: promotionItemGroup.deletedItemsId
        };
      }
    );
    return [
      {
        id: promotionGroup.id,
        type: this.convertConditionType(promotionGroup.conditionType),
        conditionItemGroups: createPromotionConditionItemGroups,
        itemGroups: createPromotionItemGroups,
        deleteConditionsItemGroupId:
          promotionGroup.deletedConditionItemGroupsId,
        deletePromotionItemGroupId: promotionGroup.deletedPromotionItemGroupsId
      }
    ];
  }
  private formatPriceConditions(
    promotionGroup: PromotionGroupForm
  ): PromotionCondition[] {
    const createPromotionCondition: PromotionCondition[] = promotionGroup.promotionConditionPrices.map(
      (promotionConditionPrice: PromotionConditionPriceForm, index: number) => {
        const type = this.convertConditionType(promotionGroup.conditionType);
        const itemGroups: PromotionItemGroups[] = promotionConditionPrice.promotionItemGroups.map(
          (promotionItemGroup: PromotionItemGroupForm) => {
            const items: PromotionItem[] = promotionItemGroup.items.map(
              (item: PromotionItemForm) => {
                return { itemSku: item.sku, quantity: item.quantity };
              }
            );
            return { items };
          }
        );
        return {
          type,
          itemGroups,
          priceRange: +promotionConditionPrice.price,
          priceOrder: index + 1
        };
      }
    );
    return createPromotionCondition;
  }
  private updateFormatPriceConditions(
    promotionGroup: PromotionGroupForm
  ): PromotionCondition[] {
    const createPromotionCondition: PromotionCondition[] = promotionGroup.promotionConditionPrices.map(
      (promotionConditionPrice: PromotionConditionPriceForm, index: number) => {
        const type = this.convertConditionType(promotionGroup.conditionType);
        const itemGroups: PromotionItemGroups[] = promotionConditionPrice.promotionItemGroups.map(
          (promotionItemGroup: PromotionItemGroupForm) => {
            const items: PromotionItem[] = promotionItemGroup.items.map(
              (item: PromotionItemForm) => {
                return {
                  id: item.id,
                  itemSku: item.sku,
                  quantity: item.quantity
                };
              }
            );
            return {
              id: promotionItemGroup.id,
              items,
              deletePromotionItemId: promotionItemGroup.deletedItemsId
            };
          }
        );

        return {
          id: promotionConditionPrice.id,
          type,
          itemGroups,
          priceRange: +promotionConditionPrice.price,
          priceOrder: index + 1,
          deletePromotionItemGroupId:
            promotionConditionPrice.deletedPromotionGroupsId
        };
      }
    );
    return createPromotionCondition;
  }
  private convertConditionType(type: string) {
    switch (type) {
      case "PRODUCT":
        return "item";
      case "PRICE":
        return "price";
      case "item":
        return "PRODUCT";
      case "price":
        return "PRICE";
      default:
        return "item";
    }
  }
  public async getPromotions(
    form: PaginationForm
  ): Promise<Pagination<Promotion>> {
    return this.client.promotions.getPromotions({
      page: form && form.page,
      limit: form && form.perPage
    });
  }

  public async getPromotion(id: number): Promise<Promotion> {
    return this.client.promotions.getPromotion(id);
  }

  public presentPromtionForm(
    promotion: any
  ): PromotionDetailForm | PromotionUpdateForm {
    const channels: number[] = promotion.channels.map((channel: any) => {
      return channel.id;
    });
    const type: string = promotion.conditions.map((condition: any) => {
      return condition.type;
    });
    let conditionItemGroups: PromotionItemGroupForm[] = [];
    let promotionItemGroups: PromotionItemGroupForm[] = [];
    let promotionConditionPrices: PromotionConditionPriceForm[] = [];
    if (type[0] === "item") {
      promotion.conditions.forEach((condition: any) => {
        conditionItemGroups = condition.conditionItemGroups.map(
          (conditionItemGroup: PromotionItemGroupForm) => {
            const items: PromotionItemForm[] = conditionItemGroup.items.map(
              (item: any) => {
                return {
                  id: item.id,
                  name: item.item.name,
                  sku: item.item.code,
                  quantity: item.quantity,
                  status: PromotionItemStatus.Success
                };
              }
            );
            return { id: conditionItemGroup.id, items, deletedItemsId: [] };
          }
        );
        promotionItemGroups = condition.itemGroups.map(
          (itemGroup: PromotionItemGroupForm) => {
            const items: PromotionItemForm[] = itemGroup.items.map(
              (item: any) => {
                return {
                  id: item.id,
                  name: item.item.name,
                  sku: item.item.code,
                  quantity: item.quantity,
                  status: PromotionItemStatus.Success
                };
              }
            );
            return { id: itemGroup.id, items, deletedItemsId: [] };
          }
        );
      });
    } else if (type[0] === "price") {
      promotionConditionPrices = promotion.conditions.map((condition: any) => {
        const promotionItemGroups = condition.itemGroups.map(
          (itemGroup: PromotionItemGroupForm) => {
            const items: PromotionItemForm[] = itemGroup.items.map(
              (item: any) => {
                return {
                  id: item.id,
                  name: item.item.name,
                  sku: item.item.code,
                  quantity: item.quantity,
                  status: PromotionItemStatus.Success
                };
              }
            );
            return { id: itemGroup.id, items, deletedItemsId: [] };
          }
        );
        return {
          id: condition.id,
          price: condition.minPrice,
          promotionItemGroups,
          deletedPromotionGroupsId: []
        };
      });
    }

    let orderItemLimit = false;
    let orderItemLimitAmount = null;
    if (promotion.orderItemLimit !== null) {
      orderItemLimit = true;
      orderItemLimitAmount = promotion.orderItemLimit;
    }

    const promotionGroup: PromotionGroupForm = {
      id: promotion.conditions[0].id,
      channelsSelected: channels,
      conditionType: this.convertConditionType(type[0]),
      conditionItemGroups,
      promotionItemGroups,
      promotionConditionPrices,
      orderLimit: +promotion.orderLimit,
      orderItemLimit,
      orderItemLimitAmount,
      promotionHold: promotion.holdWhenDepleted,
      deletedConditionItemGroupsId: [],
      deletedPromotionItemGroupsId: [],
      deletedPromotionConditonPricesId: []
    };
    return {
      name: promotion.name,
      startDate: new Date(promotion.startDate),
      endDate: promotion.endDate ? new Date(promotion.endDate) : "",
      promotionGroup,
      inuse: promotion.inuse
    };
  }

  public async testPromotion(
    form: PromotionCheckForm
  ): Promise<PromotionResult[]> {
    return this.client.promotions.checkPromotion({
      shopId: form.shop!.id,
      date: moment(form.date).toISOString(),
      channel: form.selectedChannel!,
      items: form.selectedItems.map(item => ({
        itemId: item.id!,
        quantity: item.quantity
      })),
      price: +form.totalPrice
    });
  }

  public async updatePromotion(
    id: number,
    form: PromotionUpdateForm
  ): Promise<Promotion> {
    if (
      this.convertConditionType(form.promotionGroup.conditionType) === "item"
    ) {
      let orderItemLimit: number | null = null;
      if (
        form.promotionGroup.orderItemLimit &&
        form.promotionGroup.orderItemLimitAmount !== null
      ) {
        orderItemLimit = +form.promotionGroup.orderItemLimitAmount;
      }
      return this.client.promotions.patchPromotions({
        id,
        name: form.name,
        startDate: moment(form.startDate!).toISOString(),
        endDate: form.endDate ? moment(form.endDate).toISOString() : undefined,
        orderLimit: +form.promotionGroup.orderLimit,
        orderItemLimit,
        channels: form.promotionGroup.channelsSelected,
        holdWhenDepleted: form.promotionGroup.promotionHold,
        conditions: this.updateFormatConditions(form.promotionGroup),
        inuse: form.inuse!
      });
    } else {
      return this.client.promotions.patchPromotions({
        id,
        name: form.name,
        startDate: moment(form.startDate!).toISOString(),
        endDate: form.endDate ? moment(form.endDate).toISOString() : undefined,
        orderLimit: +form.promotionGroup.orderLimit,
        channels: form.promotionGroup.channelsSelected,
        holdWhenDepleted: form.promotionGroup.promotionHold,
        conditions: this.updateFormatPriceConditions(form.promotionGroup),
        deletePromotionConditionId:
          form.promotionGroup.deletedPromotionConditonPricesId,
        inuse: form.inuse!
      });
    }
  }

  public async deletePromotion(id: number): Promise<Promotion> {
    return this.client.promotions.deletePromotions(id);
  }
}
